import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import { LoadingButton } from '@mui/lab';
import Typography from '@mui/material/Typography';
import { Autocomplete, Box, Dialog, FormHelperText, Grid } from '@mui/material';
import TextField from '@mui/material/TextField';
import { ReactComponent as ReferAFriendIcon } from '../../../../assets/common-assets/user-plus.svg';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import { EXPORT_AUTH, exportQB } from '../../../../constants/staticContent';
import { classes, AuthCardComponent } from './utils';
import { theme } from '../../../../theme';
import {
  getAllUserFromCredential,
  xeroAuthentication,
  zohoAuthentication,
} from '../../../../services/flowConfiguration';
import CustomTooltip from '../../utils/CustomTooltip';
import {
  QB_CLIENT_ID,
  THIRD_PARTY_REDIRECT_URL_URL_FOR_MANUAL_INVOICES,
  THIRD_PARTY_REDIRECT_URL_URL_FOR_MANUAL_RECEIPT,
} from '../../../../constants/Constants';
import Toast from '../../utils/Toast';
import {
  quickBookExportManually,
  odooExport,
  zohoExport,
  tallyPrimeExport,
  xeroExport,
} from '../../../../services/exportApi';
import DisplayNamePopUp from './DisplayNamePopUp';

const ExportAuthPopUp = ({
  open,
  handleClose,
  exportType,
  invoiceId,
  setOdooFormManual,
  documentType,
  setTallyPrime,
}) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState({
    id: '',
    type: '',
    label: '',
    token: '',
    display_name: '',
  });
  const [selectedAccountError, setSelectedAccountError] = useState('');
  const [openDisplayNamePopup, setOpenDisplayNamePopup] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [displayNameRequiredError, setDisplayNameRequiredError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchAllUser = async () => {
    const res = await getAllUserFromCredential(exportType);
    if (res) {
      const filteredAccounts = res.map((account) => {
        const { id, type, credential, display_name } = account;
        switch (type) {
          case 'Zoho':
            return {
              id,
              type,
              label: credential.email,
              token: credential.refresh_token,
              display_name,
            };
          case 'Odoo':
            return {
              id,
              type,
              label: credential.username,
              display_name,
            };
          case 'QuickBook':
            return {
              id,
              type,
              label: credential.email,
              token: credential.accessToken,
              display_name,
            };
          case 'TallyPrime':
            return {
              id,
              type,
              label: `${credential.ip_address}:${credential.port}`,
              token: null,
              display_name,
            };
          default:
            return {
              id,
              type,
              label: credential.email,
              token: credential.token,
              display_name,
            };
        }
      });
      setAccounts(filteredAccounts);
    }
  };
  useEffect(() => {
    if (open) {
      fetchAllUser();
    } else if (
      (selectedAccountError !== '' || selectedAccount?.label !== '') &&
      !open
    ) {
      setSelectedAccountError('');
      setSelectedAccount({
        id: '',
        type: '',
        label: '',
        token: '',
        display_name: '',
      });
    }
  }, [open]);

  const handleXeroExport = async () => {
    const invoiceIds = [invoiceId];
    setIsLoading(true);
    const response = await xeroExport(
      invoiceIds,
      exportType,
      selectedAccount?.id,
    );
    if (response) {
      setIsLoading(false);
      handleClose();
    } else {
      setIsLoading(false);
    }
  };

  const handleZohoExport = async () => {
    const invoiceIds = [invoiceId];
    setIsLoading(true);
    const response = await zohoExport(
      invoiceIds,
      exportType,
      selectedAccount?.id,
    );
    if (response) {
      setIsLoading(false);
      handleClose();
    } else {
      setIsLoading(false);
    }
  };

  const handleOdooExport = async () => {
    const invoiceIds = [invoiceId];
    setIsLoading(true);
    const response = await odooExport(
      invoiceIds,
      exportType,
      selectedAccount?.id,
    );
    if (response) {
      setIsLoading(false);
      handleClose();
    } else {
      setIsLoading(false);
    }
  };

  const handleTallyPrimeExport = async () => {
    const invoiceIds = [invoiceId];
    setIsLoading(true);
    const response = await tallyPrimeExport(
      invoiceIds,
      exportType,
      selectedAccount?.id,
    );
    if (response) {
      setIsLoading(false);
      handleClose();
    } else {
      setIsLoading(false);
    }
  };

  const handleQuickBookExport = async () => {
    const invoiceIds = [invoiceId];
    setIsLoading(true);
    const exportedInvoice = await quickBookExportManually(
      invoiceIds,
      'Manual',
      selectedAccount?.id,
      false,
    );
    if (exportedInvoice) {
      Toast({ message: exportQB.invoiceCreationSuccess, variant: 'success' });
    }
    setIsLoading(false);
    handleClose();
  };

  const exportHandler = async () => {
    if (!selectedAccount?.label) {
      setSelectedAccountError('Please choose an Account');
    } else {
      switch (exportType) {
        case 'Xero':
          await handleXeroExport();
          break;
        case 'Zoho':
          await handleZohoExport();
          break;
        case 'Odoo':
          await handleOdooExport();
          break;
        case 'QuickBook':
          await handleQuickBookExport();
          break;
        case 'TallyPrime':
          await handleTallyPrimeExport();
          break;
        default:
          break;
      }
    }
  };

  const handleSelectedAccount = (newValue) => {
    setSelectedAccount(newValue);
    setSelectedAccountError(null);
  };

  const addAccountHandler = async (selectedIconForAddAccount) => {
    const typeHandlers = {
      Xero: async () => {
        const responseUrl = await xeroAuthentication('Manual', documentType);
        if (responseUrl) {
          sessionStorage.setItem('exportOption', selectedIconForAddAccount);
          window.location.href = `${responseUrl.url}&state=${invoiceId}`;
        }
      },
      Zoho: async () => {
        const responseUrl = await zohoAuthentication('Manual', documentType);
        if (responseUrl) {
          sessionStorage.setItem('exportOption', selectedIconForAddAccount);
          window.location.href = `${responseUrl.url}&state=${invoiceId}`;
        }
      },
      QuickBook: () => {
        const { scope } = exportQB;
        const redirectURL =
          documentType === 'invoice'
            ? THIRD_PARTY_REDIRECT_URL_URL_FOR_MANUAL_INVOICES
            : THIRD_PARTY_REDIRECT_URL_URL_FOR_MANUAL_RECEIPT;
        const authUri = `https://appcenter.intuit.com/connect/oauth2?client_id=${QB_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(redirectURL)}&scope=${encodeURIComponent(scope)}&state=${invoiceId}`;
        window.open(authUri, '_self');
      },
      Odoo: () => {
        setOdooFormManual(true);
      },
      TallyPrime: () => {
        setTallyPrime(true);
      },
    };

    const handler = typeHandlers[selectedIconForAddAccount];
    if (handler) {
      await handler();
    }
  };

  const addAccountDisplayName = async () => {
    if (exportType === 'TallyPrime' || exportType === 'Odoo') {
      await addAccountHandler(exportType);
    } else {
      setDisplayName('');
      setOpenDisplayNamePopup(true);
    }
  };

  const handleDisplayNamePopupClose = () => {
    setOpenDisplayNamePopup(false);
    setDisplayNameRequiredError('');
  };

  return (
    <Dialog open={open}>
      <AuthCardComponent
        className={
          selectedAccountError
            ? classes.errorCardComponent
            : classes.cardComponent
        }
      >
        <CardHeader
          className={classes.cardHeader}
          title={
            <Grid container alignItems="center">
              <Grid item xs={10}>
                <Typography variant="h6">{`Export in ${exportType}`}</Typography>
              </Grid>
              <Grid item xs={2} align="right">
                <Avatar
                  onClick={handleClose}
                  src={closeIcon}
                  className={classes.closeAction}
                />
              </Grid>
            </Grid>
          }
        />

        <CardContent className={classes.cardContent}>
          <Typography
            variant="subtitle1"
            className={classes.subTitle}
            sx={{
              color: theme.palette.shoeWax.dark,
              marginBottom: theme.spacing(1.5),
            }}
          >
            {exportType === 'TallyPrime'
              ? EXPORT_AUTH.SUB_TITLE_TALLY
              : EXPORT_AUTH.SUB_TITLE}
          </Typography>
          <Typography
            variant="subtitle1"
            className={classes.exportAccountTypography}
          >
            {`${exportType} ${exportType === 'TallyPrime' ? 'URL' : 'Accounts'}`}
          </Typography>

          <Box display="flex" alignItems="center">
            <Box width="90%">
              <Autocomplete
                fullWidth
                value={selectedAccount}
                options={accounts}
                loading={!(accounts.length > 0)}
                size="small"
                getOptionLabel={(option) =>
                  option.display_name ? option.display_name : option.label
                }
                onChange={(event, newValue) => {
                  handleSelectedAccount(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Account" />
                )}
              />
            </Box>
            <Box width="10%" ml={1}>
              <CustomTooltip title="Add account">
                <ReferAFriendIcon
                  onClick={addAccountDisplayName}
                  sx={{ cursor: 'pointer' }}
                />
              </CustomTooltip>
            </Box>
          </Box>
          {selectedAccountError && (
            <FormHelperText className={classes.formHelperText}>
              {selectedAccountError}
            </FormHelperText>
          )}

          <Box display="flex" justifyContent="end" alignItems="end" gap={1}>
            <LoadingButton
              variant="contained"
              alignItems="center"
              className={classes.exportButton}
              onClick={exportHandler}
              loading={isLoading}
            >
              {EXPORT_AUTH.EXPORT_BUTTON}
            </LoadingButton>
          </Box>
        </CardContent>
      </AuthCardComponent>
      <DisplayNamePopUp
        open={openDisplayNamePopup}
        handleClose={handleDisplayNamePopupClose}
        setDisplayName={setDisplayName}
        displayName={displayName}
        displayNameRequiredError={displayNameRequiredError}
        setDisplayNameRequiredError={setDisplayNameRequiredError}
        addAccountHandler={addAccountHandler}
        selectedIcon={exportType}
      />
    </Dialog>
  );
};

ExportAuthPopUp.propTypes = {
  open: PropTypes.bool.isRequired,
  setOdooFormManual: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  exportType: PropTypes.string.isRequired,
  invoiceId: PropTypes.string.isRequired,
  documentType: PropTypes.string.isRequired,
  setTallyPrime: PropTypes.func.isRequired,
};

export default ExportAuthPopUp;
